import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { authApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, Check, X } from "lucide-react";
import { cn } from "@/lib/utils";

function PasswordInput({
  id,
  value,
  onChange,
  placeholder,
  isDark,
  autoComplete,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  isDark: boolean;
  autoComplete?: string;
}) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input
        id={id}
        type={show ? "text" : "password"}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        className={cn(
          "pr-10",
          isDark
            ? "border-white/10 bg-white/5 text-white placeholder:text-slate-500"
            : "border-slate-300 bg-white text-slate-900 placeholder:text-slate-400"
        )}
      />
      <button
        type="button"
        onClick={() => setShow(!show)}
        className={cn(
          "absolute right-3 top-1/2 -translate-y-1/2 transition-colors",
          isDark ? "text-slate-500 hover:text-slate-300" : "text-slate-400 hover:text-slate-600"
        )}
      >
        {show ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </div>
  );
}

function PasswordRules({ password, isDark, t }: { password: string; isDark: boolean; t: (k: string) => string }) {
  const rules = [
    { key: "min8", label: t("changePwd.rule.min8"), test: password.length >= 8 },
    { key: "upper", label: t("changePwd.rule.upper"), test: /[A-Z]/.test(password) },
    { key: "lower", label: t("changePwd.rule.lower"), test: /[a-z]/.test(password) },
    { key: "digit", label: t("changePwd.rule.digit"), test: /[0-9]/.test(password) },
  ];
  return (
    <div className="space-y-1 mt-2">
      {rules.map((r) => (
        <div key={r.key} className="flex items-center gap-1.5 text-xs">
          {r.test
            ? <Check size={12} className="text-emerald-400" />
            : <X size={12} className={isDark ? "text-slate-600" : "text-slate-400"} />}
          <span className={r.test ? "text-emerald-400" : (isDark ? "text-slate-500" : "text-slate-400")}>{r.label}</span>
        </div>
      ))}
    </div>
  );
}

function isPasswordValid(password: string): boolean {
  return (
    password.length >= 8 &&
    /[A-Z]/.test(password) &&
    /[a-z]/.test(password) &&
    /[0-9]/.test(password)
  );
}

export default function ChangePassword() {
  const [, navigate] = useLocation();
  const { user, isAuthenticated, isLoading, refreshUser, logout } = useAuth();
  const { platformTheme } = useTheme();
  const { t } = useLanguage();
  const isDark = platformTheme === "tech";

  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate("/login");
    }
  }, [isLoading, isAuthenticated, navigate]);

  if (isLoading || !isAuthenticated) {
    return (
      <div className={cn("flex h-screen w-full items-center justify-center", isDark ? "bg-[#0a0e1a]" : "bg-slate-50")}>
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
      </div>
    );
  }

  const mustChange = !!user?.must_change_password;
  const passwordValid = isPasswordValid(newPassword);
  const passwordsMatch = newPassword === confirmPassword && confirmPassword.length > 0;
  const canSubmit = oldPassword.length > 0 && passwordValid && passwordsMatch && newPassword !== oldPassword;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!oldPassword || !newPassword || !confirmPassword) {
      toast.error(t("changePwd.fillAll"));
      return;
    }
    if (!passwordValid) {
      toast.error(t("changePwd.tooShort"));
      return;
    }
    if (newPassword === oldPassword) {
      toast.error(t("changePwd.sameAsOld"));
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error(t("changePwd.mismatch"));
      return;
    }
    setSubmitting(true);
    try {
      await authApi.changePassword(oldPassword, newPassword);
      toast.success(t("changePwd.success"));
      await refreshUser();
      navigate("/console/dashboard");
    } catch (err: any) {
      const msg =
        err?.response?.data?.message || err?.message || "Failed to change password";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={cn(
      "flex min-h-screen items-center justify-center px-4",
      isDark ? "bg-[#0a0e1a]" : "bg-slate-50"
    )}>
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <div
            className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl shadow-lg shadow-blue-500/20"
            style={{
              background:
                "linear-gradient(135deg, #2563EB 0%, #06B6D4 50%, #818CF8 100%)",
            }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </div>
          <h1
            className={cn("text-2xl font-bold", isDark ? "text-white" : "text-slate-900")}
            style={{ fontFamily: "Space Grotesk, sans-serif" }}
          >
            {t("changePwd.title")}
          </h1>
          {mustChange && (
            <p className="mt-2 text-sm text-amber-400">
              {t("changePwd.mustChange")}
            </p>
          )}
        </div>

        <div className={cn(
          "rounded-xl border p-6",
          isDark
            ? "border-white/10 bg-white/5 backdrop-blur-sm"
            : "border-slate-200 bg-white shadow-xl shadow-slate-200/60"
        )}>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="cp-old" className={isDark ? "text-slate-300" : "text-slate-700"}>
                {t("changePwd.currentPassword")}
              </Label>
              <PasswordInput
                id="cp-old"
                value={oldPassword}
                onChange={setOldPassword}
                isDark={isDark}
                autoComplete="current-password"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cp-new" className={isDark ? "text-slate-300" : "text-slate-700"}>
                {t("changePwd.newPassword")}
              </Label>
              <PasswordInput
                id="cp-new"
                value={newPassword}
                onChange={setNewPassword}
                placeholder={t("changePwd.placeholder")}
                isDark={isDark}
                autoComplete="new-password"
              />
              {newPassword.length > 0 && (
                <PasswordRules password={newPassword} isDark={isDark} t={t} />
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="cp-confirm" className={isDark ? "text-slate-300" : "text-slate-700"}>
                {t("changePwd.confirmPassword")}
              </Label>
              <PasswordInput
                id="cp-confirm"
                value={confirmPassword}
                onChange={setConfirmPassword}
                isDark={isDark}
                autoComplete="new-password"
              />
              {confirmPassword.length > 0 && (
                <div className="flex items-center gap-1.5 text-xs mt-1">
                  {passwordsMatch
                    ? <><Check size={12} className="text-emerald-400" /><span className="text-emerald-400">{t("changePwd.match")}</span></>
                    : <><X size={12} className="text-red-400" /><span className="text-red-400">{t("changePwd.noMatch")}</span></>}
                </div>
              )}
            </div>
            <Button
              type="submit"
              className="w-full btn-primary-sci"
              disabled={submitting || !canSubmit}
            >
              {submitting ? t("changePwd.submitting") : t("changePwd.submit")}
            </Button>
            {!mustChange && (
              <Button
                type="button"
                variant="ghost"
                className={cn("w-full", isDark ? "text-slate-400 hover:text-white" : "text-slate-500 hover:text-slate-900")}
                onClick={() => navigate("/console/dashboard")}
              >
                {t("changePwd.cancel")}
              </Button>
            )}
            {mustChange && (
              <Button
                type="button"
                variant="ghost"
                className={cn("w-full", isDark ? "text-slate-400 hover:text-white" : "text-slate-500 hover:text-slate-900")}
                onClick={() => logout()}
              >
                {t("changePwd.signOut")}
              </Button>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
